import * as React from 'react';
import {useContext, useEffect, useState} from "react";
import Dump from "../components/dump.tsx";
import Pagination, {getTotalPages} from "../components/pagination";
import axios from 'axios';
import DialogStyled from "../components/dialogStyled";
import Asset from './asset';
import AssetEdit from "./assetEdit";
import {findElementWithIdInArray, isFunction} from "../helpers/utils";
import {confirm} from "../helpers/confirmDelete";
import {ContextUrls} from "../state/contextUrlApi";
import Spinner from "../components/spinner";
import type { AssetData } from "./asset.interface";
import Upload from "./upload";

type Props = {
	perPage?: number,
	editable?: string|boolean,
	uploadEnabled?: string|boolean,
	onSelectId?: Function|string,
	onSelect?: Function,
	objectId?: number|'all'
}

function MediaLibrary({
	perPage = 12,
	onSelectId,
	onSelect,
	objectId,
	editable = false,
	uploadEnabled = false,
	...otherProps
}: Props) {

	const urls = useContext(ContextUrls);

	const [currentPage, setCurrentPage] = useState<number>(0);
	const [assetCount, setAssetCount] = useState<number>(1);
	const [search, setSearch] = useState<string>("");

	const [assets, setAssets] = useState<[AssetData]|null>(null);

	const [assetEdited, setAssetEdited] = useState<AssetData|null>(null);
	const [showModalEdit, setShowModalEdit] = useState<boolean>(false);

	function onPageChange(page: number) {
		setCurrentPage(page);
	}

	function closeModalEdit() {
		setAssetEdited(null);
		setShowModalEdit(false);
	}

	function openModalEdit(id: number) {
		setAssetEdited(findElementWithIdInArray(assets, id));
		setShowModalEdit(true);
	}

	function validateCurrentPage(currentPage: number): number {
		return (currentPage < 0) ? 0 : currentPage;
	}

	async function fetchAssets() {
		let toSend = {
			page: validateCurrentPage(currentPage),
			perPage: perPage,
			objectId: null,
			search: ''
		};

		if (objectId) {
			toSend.objectId = objectId;
		}

		if (search && search.length > 1) {
			toSend.search = search;
		}

		try {
			const response = await axios.get(urls.asset.url, {params: toSend});

			setAssets(response.data.list);
			setAssetCount(response.data.count);
		} catch (error) {
			console.log('axios error: ', error);
		}
	}

	useEffect(() => {
		fetchAssets();
	}, [perPage, currentPage, search]);


	async function handleDelete(id: number) {
		if (await confirm('Delete file', {description: 'This cannot be undone.'})) {
			axios.delete(urls.asset.url + '/' + id)
				.then(() => fetchAssets());
		}
	}

	function getSelectIdHandler(asset: object) {
		if (isFunction(onSelect)) {
			return () => onSelect(asset);
		} else if (isFunction(onSelectId)) {
			// @ts-ignore
			return () => onSelectId(asset.id);
		} else if (typeof onSelectId === 'string' && window.hasOwnProperty(onSelectId)) {
			// @ts-ignore
			return () => window[onSelectId](asset.id);
		}
		return null;
	}

	return (
		<>
			<div className="flex justify-between items-center my-4">
				<div className="min-w-[235px]"/>

				<Pagination
					currentPage={currentPage}
					setCurrentPage={onPageChange}
					totalPages={getTotalPages(assetCount, perPage)}
				/>

				<div className="min-w-[235px] text-right">
					<input
						type="search"
						placeholder="Search files"
						value={search}
						onChange={event => setSearch(event.target.value) }
					/>
				</div>
			</div>

			{assets === null && <div className="text-center my-80"><Spinner/></div>}

			<div className="flex flex-wrap -mx-2.5 my-2.5 items-stretch">
				{assets && assets.map(asset => {
					return (
						<div key={asset.id} className="w-full md:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/6 p-2.5">
							<Asset
								openModalEdit={editable ? () => openModalEdit(asset.id) : undefined}
								handleDelete={editable ? () => handleDelete(asset.id) : undefined}
								onSelect={getSelectIdHandler(asset)}
								asset={asset}
							/>
						</div>
					);
				})}
			</div>

			{uploadEnabled &&
				<div className="max-w-screen-lg mx-auto">
					<Upload
					    objId={objectId && objectId !== 'all' ? objectId : null}
						onComplete={() => {
							fetchAssets();
							setCurrentPage(0);
						}}
					/>
				</div>
			}

			<div className="text-center mt-6 mb-8">
				<Pagination
					currentPage={currentPage}
					setCurrentPage={onPageChange}
					totalPages={getTotalPages(assetCount, perPage)}
				/>
			</div>

			{/*<Dump value={filteredProducts}/>*/}

			<DialogStyled
				isOpen={showModalEdit}
				onClose={closeModalEdit}
				classesSize={"max-w-[90vw] max-h-[97vh]"}
				zIndex={50}
			>
				{assetEdited && <AssetEdit
					asset={assetEdited}
					handleClose={closeModalEdit}
					onAfterSave={() => fetchAssets()}
				/>}
			</DialogStyled>

			<Dump {...otherProps}/>
			<Dump label="perPage" value={perPage}/>
			<Dump label="editable" value={editable} />
			<Dump label="uploadEnabled" value={uploadEnabled}/>
			<Dump label="Urls" {...urls}/>
		</>
	);
}

export default MediaLibrary;
